import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatPlayTime } from '@/lib/heartbeat';
import {
  Users,
  Save,
  Bug,
  BarChart3,
  Plus,
  Trash2,
  RefreshCcw,
  Download,
  Power,
  X,
  LogOut,
} from 'lucide-react';
import {
  getAdminPassword,
  adminAuthHeaders,
  clearAdminPassword,
} from './adminAuth';

interface BetaTester {
  code: string;
  name: string;
  email?: string;
  active: boolean;
  createdAt: number;
  lastSeenAt: number | null;
  notes?: string;
}

interface CloudSavegameMeta {
  testerCode: string;
  slotIndex: number;
  data: unknown;
  updatedAt: number;
  version: string;
  size: number;
}

interface RawLog {
  trackingId?: string;
  type?: string;
  timestamp?: number;
  pseudonym?: string;
  version?: string;
  expected?: string;
  actual?: string;
  message?: string;
  reason?: string;
  source?: string;
  [k: string]: unknown;
}

type Tab = 'testers' | 'savegames' | 'logs' | 'stats';

/**
 * Phase 16.5.4 — Admin-Dashboard.
 *
 * Tabs:
 *   - Tester (Tabelle mit CRUD)
 *   - Spielstände (Tester wählen → 3 Slots inkl. Download)
 *   - Bug-Reports (existierende /api/admin/logs unter Admin-Auth)
 *   - Statistiken (aggregierte Zahlen)
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const password = getAdminPassword();
  const [tab, setTab] = useState<Tab>('testers');

  if (!password) return <Navigate to="/admin" replace />;

  return (
    <main className="min-h-screen bg-bg-primary px-4 py-6 text-fg-primary md:px-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Admin-Dashboard</h1>
            <p className="text-sm text-fg-muted">Closed-Beta-Verwaltung</p>
          </div>
          <button
            type="button"
            onClick={() => {
              clearAdminPassword();
              navigate('/admin');
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm font-semibold text-fg-secondary transition hover:bg-bg-elevated/70 hover:text-fg-primary"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden /> Abmelden
          </button>
        </header>

        <nav
          role="tablist"
          aria-label="Admin-Tabs"
          className="mb-6 flex flex-wrap gap-1 rounded-md border border-border-subtle bg-bg-secondary p-1"
        >
          <TabButton id="testers" current={tab} onClick={setTab} icon={Users} label="Tester" />
          <TabButton id="savegames" current={tab} onClick={setTab} icon={Save} label="Spielstände" />
          <TabButton id="logs" current={tab} onClick={setTab} icon={Bug} label="Bug-Reports" />
          <TabButton id="stats" current={tab} onClick={setTab} icon={BarChart3} label="Statistik" />
        </nav>

        {tab === 'testers' && <TestersTab />}
        {tab === 'savegames' && <SavegamesTab />}
        {tab === 'logs' && <LogsTab />}
        {tab === 'stats' && <StatsTab />}
      </div>
    </main>
  );
}

function TabButton({
  id,
  current,
  onClick,
  icon: Icon,
  label,
}: {
  id: Tab;
  current: Tab;
  onClick: (next: Tab) => void;
  icon: typeof Users;
  label: string;
}) {
  const active = current === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onClick(id)}
      className={[
        'flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-sm font-semibold transition',
        active ? 'bg-accent-dispatch text-white shadow-sm' : 'text-fg-muted hover:text-fg-primary',
      ].join(' ')}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

/* ─────────────── Tester-Tab ─────────────── */

interface TesterStatsRow {
  code: string;
  name: string;
  active: boolean;
  isOnline: boolean;
  lastSeenAt: number | null;
  playTime: { totalSec: number; todaySec: number; last7DaysSec: number };
  sessions: { total: number; today: number; last7Days: number };
  slotsCreated: number;
  missionsCompleted: number;
  missionsFailed: number;
  lastMissionAt: number | null;
  dailyPlayTime: Array<{ date: string; sec: number }>;
  dailySessions: Array<{ date: string; count: number }>;
}

const AUTO_REFRESH_MS = 30_000;

function TestersTab() {
  const [rows, setRows] = useState<TesterStatsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [, setNowTick] = useState(0);

  const fetchStats = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/api/admin/tester-stats', { headers: adminAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { testers: TesterStatsRow[] };
      // Sortierung: Online zuerst, dann nach Spielzeit-gesamt absteigend
      const sorted = [...data.testers].sort((a, b) => {
        if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
        return b.playTime.totalSec - a.playTime.totalSec;
      });
      setRows(sorted);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStats();
    const id = setInterval(() => void fetchStats(), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchStats]);

  // 1-Min-Tick für die "vor X Min"-Relativzeit
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const toggleActive = async (row: TesterStatsRow) => {
    try {
      const res = await fetch(`/api/admin/testers?code=${row.code}`, {
        method: 'PATCH',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !row.active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchStats();
    } catch (e) {
      window.alert(`Fehler: ${String(e)}`);
    }
  };

  const deleteTester = async (row: TesterStatsRow) => {
    if (
      !window.confirm(
        `Tester "${row.name}" (${row.code}) UND alle Spielstände wirklich löschen?`,
      )
    )
      return;
    try {
      const res = await fetch(`/api/admin/testers?code=${row.code}`, {
        method: 'DELETE',
        headers: adminAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchStats();
    } catch (e) {
      window.alert(`Fehler: ${String(e)}`);
    }
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-fg-muted">
          {loading
            ? 'Lade …'
            : `${rows.length} Tester · ${rows.filter((r) => r.isOnline).length} online · Auto-Refresh alle 30 s`}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchStats}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm font-semibold transition hover:bg-bg-elevated/70"
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden /> Aktualisieren
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-emergency px-3 py-2 text-sm font-semibold text-white transition hover:bg-accent-emergency-hover"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Neuer Tester
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-accent-emergency/40 bg-accent-emergency/10 p-3 text-sm text-accent-emergency">
          Fehler: {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border-subtle bg-bg-secondary">
        <table className="w-full text-left text-sm">
          <thead className="bg-bg-primary/40 text-xs uppercase tracking-wider text-fg-muted">
            <tr>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Code / Name</th>
              <th className="px-3 py-3 text-right">Heute</th>
              <th className="px-3 py-3 text-right">7 Tage</th>
              <th className="px-3 py-3 text-right">Gesamt</th>
              <th className="px-3 py-3 text-right">Sessions</th>
              <th className="px-3 py-3 text-right">Slots</th>
              <th className="px-3 py-3 text-right">Missions</th>
              <th className="px-3 py-3">Letzte Aktivität</th>
              <th className="px-3 py-3 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = expanded === r.code;
              return (
                <Fragment key={r.code}>
                  <tr
                    className={[
                      'cursor-pointer border-t border-border-subtle transition',
                      isOpen ? 'bg-bg-elevated/40' : 'hover:bg-bg-elevated/30',
                    ].join(' ')}
                    onClick={() => setExpanded(isOpen ? null : r.code)}
                    data-testid={`tester-row-${r.code}`}
                  >
                    <td className="px-3 py-3">
                      <span className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className={[
                            'inline-block h-2 w-2 rounded-full',
                            r.isOnline ? 'bg-status-available animate-pulse' : 'bg-fg-muted/40',
                          ].join(' ')}
                        />
                        <span
                          className={[
                            'text-[10px] font-semibold uppercase tracking-wider',
                            r.isOnline ? 'text-status-available' : 'text-fg-muted',
                          ].join(' ')}
                        >
                          {r.isOnline ? 'aktiv' : 'offline'}
                        </span>
                        {!r.active && (
                          <span className="ml-1 rounded bg-accent-emergency/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-emergency">
                            gesperrt
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-mono text-xs">{r.code}</div>
                      <div className="text-fg-muted">{r.name}</div>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {formatPlayTime(r.playTime.todaySec)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {formatPlayTime(r.playTime.last7DaysSec)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs font-semibold text-fg-primary">
                      {formatPlayTime(r.playTime.totalSec)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {r.sessions.total}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {r.slotsCreated}
                    </td>
                    <td className="px-3 py-3 text-right text-xs">
                      <span className="font-mono text-status-available">
                        {r.missionsCompleted}
                      </span>
                      <span className="text-fg-muted"> / </span>
                      <span className="font-mono text-accent-emergency">
                        {r.missionsFailed}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-fg-muted">
                      {r.lastSeenAt
                        ? formatRelativeTime(r.lastSeenAt)
                        : '—'}
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => void toggleActive(r)}
                        aria-label={r.active ? 'Deaktivieren' : 'Aktivieren'}
                        title={r.active ? 'Deaktivieren' : 'Aktivieren'}
                        className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-elevated hover:text-fg-primary"
                      >
                        <Power className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteTester(r)}
                        aria-label="Löschen"
                        title="Löschen"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-accent-emergency/20 hover:text-accent-emergency"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-border-subtle bg-bg-elevated/20">
                      <td colSpan={10} className="px-5 py-4">
                        <TesterDetailExpansion row={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-sm text-fg-muted">
                  Noch keine Tester. „Neuer Tester" klicken zum Anlegen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateTesterModal
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await fetchStats();
          }}
        />
      )}
    </section>
  );
}

function TesterDetailExpansion({ row }: { row: TesterStatsRow }) {
  const last7Days = useMemo(() => buildLast7DaysWindow(), []);
  const playByDate = new Map(row.dailyPlayTime.map((d) => [d.date, d.sec]));
  const sessByDate = new Map(row.dailySessions.map((d) => [d.date, d.count]));
  const playValues = last7Days.map((d) => playByDate.get(d) ?? 0);
  const sessValues = last7Days.map((d) => sessByDate.get(d) ?? 0);
  const maxPlay = Math.max(1, ...playValues);
  const maxSess = Math.max(1, ...sessValues);

  const total = row.missionsCompleted + row.missionsFailed;
  const successPct = total > 0 ? Math.round((row.missionsCompleted / total) * 100) : 0;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-fg-muted">
          Spielzeit · letzte 7 Tage
        </p>
        <div className="flex h-16 items-end gap-1">
          {last7Days.map((date, i) => (
            <div key={date} className="flex flex-1 flex-col items-center gap-1" title={`${date}: ${formatPlayTime(playValues[i])}`}>
              <div
                className="w-full rounded-sm bg-accent-dispatch"
                style={{ height: `${(playValues[i] / maxPlay) * 100}%` }}
              />
              <span className="text-[9px] text-fg-muted">{date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-fg-muted">
          Sessions · letzte 7 Tage
        </p>
        <div className="flex h-16 items-end gap-1">
          {last7Days.map((date, i) => (
            <div key={date} className="flex flex-1 flex-col items-center gap-1" title={`${date}: ${sessValues[i]} Sessions`}>
              <div
                className="w-full rounded-sm bg-accent-emergency"
                style={{ height: `${(sessValues[i] / maxSess) * 100}%` }}
              />
              <span className="text-[9px] text-fg-muted">{date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-fg-muted">Missions</p>
        {total === 0 ? (
          <p className="text-xs text-fg-muted">Noch keine Mission abgeschlossen.</p>
        ) : (
          <>
            <p className="text-sm">
              <span className="font-mono font-semibold text-status-available">
                {row.missionsCompleted}
              </span>{' '}
              erfolgreich · <span className="font-mono text-accent-emergency">{row.missionsFailed}</span>{' '}
              fehlgeschlagen
            </p>
            <p className="mt-1 text-xs text-fg-muted">Erfolgsquote: {successPct} %</p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-bg-primary">
              <div
                className="h-full rounded-full bg-status-available"
                style={{ width: `${successPct}%` }}
              />
            </div>
          </>
        )}
        <p className="mt-3 text-xs text-fg-muted">
          {row.lastMissionAt
            ? `Letzte Mission-Aktivität ${formatRelativeTime(row.lastMissionAt)}`
            : 'Noch keine Mission-Aktivität'}
        </p>
      </div>
    </div>
  );
}

function buildLast7DaysWindow(): string[] {
  const out: string[] = [];
  const now = Date.now();
  for (let i = 6; i >= 0; i--) {
    out.push(new Date(now - i * 86_400_000).toISOString().split('T')[0]);
  }
  return out;
}

function formatRelativeTime(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 30) return 'gerade eben';
  if (sec < 60) return `vor ${sec} s`;
  if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min`;
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)} h`;
  return `vor ${Math.floor(sec / 86400)} d`;
}

function CreateTesterModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/testers', {
        method: 'POST',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim() || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { tester: BetaTester };
      setCreatedCode(data.tester.code);
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay/80 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border-strong bg-bg-secondary shadow-2xl">
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <p className="font-display text-lg font-semibold">Neuer Tester</p>
          <button type="button" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4 text-fg-muted hover:text-fg-primary" aria-hidden />
          </button>
        </header>

        {createdCode ? (
          <div className="space-y-4 px-5 py-5">
            <p className="text-sm">Tester wurde angelegt. Sein Code:</p>
            <p className="rounded-md bg-bg-elevated p-3 text-center font-mono text-lg tracking-wider text-status-available">
              {createdCode}
            </p>
            <p className="text-xs text-fg-muted">
              Schick den Code an den Tester. Er kann sich damit auf <code>/play</code> einloggen.
            </p>
            <button
              type="button"
              onClick={onCreated}
              className="w-full rounded-md bg-accent-emergency px-4 py-2.5 text-sm font-semibold text-white"
            >
              Fertig
            </button>
          </div>
        ) : (
          <div className="space-y-3 px-5 py-5">
            <Field label="Name" required>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
                placeholder="z. B. Mark"
              />
            </Field>
            <Field label="Email" hint="optional">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
                placeholder="mark@example.com"
              />
            </Field>
            {error && (
              <p className="text-xs text-accent-emergency">Fehler: {error}</p>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={!name.trim() || submitting}
              className="w-full rounded-md bg-accent-emergency px-4 py-2.5 text-sm font-semibold text-white disabled:bg-bg-elevated disabled:text-fg-muted"
            >
              {submitting ? 'Erstelle …' : 'Code generieren'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold">
          {label}
          {required && <span className="ml-1 text-accent-emergency">*</span>}
        </p>
        {hint && <p className="text-[10px] text-fg-muted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

/* ─────────────── Spielstände-Tab ─────────────── */

function SavegamesTab() {
  const [testers, setTesters] = useState<BetaTester[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [slots, setSlots] = useState<
    Array<{ slotIndex: number; savegame: CloudSavegameMeta | null }>
  >([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetch('/api/admin/testers', { headers: adminAuthHeaders() });
      if (res.ok) {
        const data = (await res.json()) as { testers: BetaTester[] };
        setTesters(data.testers);
      }
    })();
  }, []);

  const loadSlots = async (code: string) => {
    setSelectedCode(code);
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/savegames?code=${code}`, {
        headers: adminAuthHeaders(),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          slots: Array<{ slotIndex: number; savegame: CloudSavegameMeta | null }>;
        };
        setSlots(data.slots);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadSlot = (slot: { slotIndex: number; savegame: CloudSavegameMeta | null }) => {
    if (!slot.savegame) return;
    const blob = new Blob([JSON.stringify(slot.savegame, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedCode}-slot${slot.slotIndex}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteSlot = async (slotIndex: number) => {
    if (!selectedCode) return;
    if (!window.confirm(`Slot ${slotIndex} von ${selectedCode} löschen?`)) return;
    const res = await fetch(`/api/admin/savegames?code=${selectedCode}&slot=${slotIndex}`, {
      method: 'DELETE',
      headers: adminAuthHeaders(),
    });
    if (res.ok) await loadSlots(selectedCode);
  };

  return (
    <section className="grid gap-4 md:grid-cols-[260px_1fr]">
      <aside className="rounded-lg border border-border-subtle bg-bg-secondary p-3">
        <p className="mb-2 text-xs uppercase tracking-wider text-fg-muted">Tester wählen</p>
        <ul className="space-y-1 text-sm">
          {testers.map((t) => (
            <li key={t.code}>
              <button
                type="button"
                onClick={() => void loadSlots(t.code)}
                className={[
                  'block w-full truncate rounded px-2 py-1.5 text-left transition',
                  selectedCode === t.code
                    ? 'bg-accent-dispatch text-white'
                    : 'hover:bg-bg-elevated',
                ].join(' ')}
              >
                <span className="font-mono text-xs">{t.code}</span>
                <span className="ml-2 text-fg-muted">{t.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div>
        {!selectedCode && (
          <p className="rounded-md border border-border-subtle bg-bg-secondary p-4 text-sm text-fg-muted">
            Tester links wählen, um Spielstände zu sehen.
          </p>
        )}
        {selectedCode && loading && <p className="text-sm text-fg-muted">Lade Slots …</p>}
        {selectedCode && !loading && (
          <div className="grid gap-3 md:grid-cols-3">
            {slots.map((slot) => (
              <article
                key={slot.slotIndex}
                className="rounded-lg border border-border-subtle bg-bg-secondary p-4"
              >
                <p className="text-xs uppercase tracking-wider text-fg-muted">
                  Slot {slot.slotIndex + 1}
                </p>
                {slot.savegame ? (
                  <>
                    <p className="mt-1 font-mono text-xs">v{slot.savegame.version}</p>
                    <p className="mt-1 text-xs text-fg-muted">
                      {(slot.savegame.size / 1024).toFixed(1)} kB ·{' '}
                      {new Date(slot.savegame.updatedAt).toLocaleString()}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => downloadSlot(slot)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-accent-dispatch px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                      >
                        <Download className="h-3 w-3" aria-hidden /> JSON
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteSlot(slot.slotIndex)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border-subtle px-2 py-1.5 text-xs font-semibold text-fg-muted hover:bg-accent-emergency/20 hover:text-accent-emergency"
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="mt-2 text-xs text-fg-muted">leer</p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────── Logs-Tab ─────────────── */

function LogsTab() {
  const [logs, setLogs] = useState<RawLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/admin/logs?limit=200', {
          headers: adminAuthHeaders(),
        });
        if (res.ok) {
          const data = (await res.json()) as { logs: RawLog[] };
          setLogs(data.logs);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section>
      <p className="mb-3 text-sm text-fg-muted">
        {loading ? 'Lade …' : `${logs.length} Bug-Reports + Auto-Errors (letzte 200)`}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border-subtle bg-bg-secondary">
        <table className="w-full text-left text-sm">
          <thead className="bg-bg-primary/40 text-xs uppercase tracking-wider text-fg-muted">
            <tr>
              <th className="px-4 py-3">Zeit</th>
              <th className="px-4 py-3">Typ</th>
              <th className="px-4 py-3">Pseudonym</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3">Beschreibung</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr
                key={(l.trackingId ?? '') + i}
                className="border-t border-border-subtle hover:bg-bg-elevated/30"
              >
                <td className="px-4 py-3 font-mono text-xs">
                  {l.timestamp ? new Date(l.timestamp).toISOString().slice(0, 19) : '—'}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      l.type === 'bug-report'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'bg-accent-emergency/20 text-accent-emergency',
                    ].join(' ')}
                  >
                    {l.type ?? '?'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{l.pseudonym ?? '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.version ?? '—'}</td>
                <td className="max-w-md truncate px-4 py-3 text-xs">
                  {l.actual ?? l.message ?? l.reason ?? l.source ?? ''}
                </td>
              </tr>
            ))}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-fg-muted">
                  Noch keine Logs.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─────────────── Stats-Tab ─────────────── */

function StatsTab() {
  const [testers, setTesters] = useState<BetaTester[]>([]);
  const [logs, setLogs] = useState<RawLog[]>([]);

  useEffect(() => {
    void Promise.all([
      fetch('/api/admin/testers', { headers: adminAuthHeaders() })
        .then((r) => r.json())
        .then((d: { testers: BetaTester[] }) => setTesters(d.testers))
        .catch(() => {}),
      fetch('/api/admin/logs?limit=500', { headers: adminAuthHeaders() })
        .then((r) => r.json())
        .then((d: { logs: RawLog[] }) => setLogs(d.logs))
        .catch(() => {}),
    ]);
  }, []);

  const stats = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 86_400_000;
    const activeLast7d = testers.filter(
      (t) => t.lastSeenAt !== null && t.lastSeenAt >= sevenDaysAgo,
    ).length;
    const bugReports = logs.filter((l) => l.type === 'bug-report').length;
    const autoErrors = logs.filter((l) => l.type === 'auto-error').length;
    // Häufigste Bugs (nach actual-Feld, top 5)
    const counts = new Map<string, number>();
    for (const l of logs) {
      const key = (l.actual ?? l.message ?? l.reason ?? '').slice(0, 60);
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const topBugs = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      totalTesters: testers.length,
      activeTesters: testers.filter((t) => t.active).length,
      activeLast7d,
      bugReports,
      autoErrors,
      topBugs,
    };
  }, [testers, logs]);

  return (
    <section className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Tester gesamt" value={String(stats.totalTesters)} />
        <StatCard label="Aktive Tester" value={String(stats.activeTesters)} />
        <StatCard label="Aktiv (7 Tage)" value={String(stats.activeLast7d)} />
        <StatCard label="Bug-Reports" value={String(stats.bugReports)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard label="Auto-Errors" value={String(stats.autoErrors)} />
        <article className="rounded-lg border border-border-subtle bg-bg-secondary p-4">
          <p className="mb-2 text-xs uppercase tracking-wider text-fg-muted">Top-Bugs</p>
          {stats.topBugs.length === 0 ? (
            <p className="text-sm text-fg-muted">Keine Bug-Daten</p>
          ) : (
            <ol className="space-y-1 text-xs">
              {stats.topBugs.map(([msg, count], i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="font-mono text-fg-muted">{count}×</span>
                  <span className="truncate">{msg}</span>
                </li>
              ))}
            </ol>
          )}
        </article>
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <article className="rounded-lg border border-border-subtle bg-bg-secondary p-4">
      <p className="text-xs uppercase tracking-wider text-fg-muted">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold tabular-nums">{value}</p>
    </article>
  );
}
